import React, { useEffect, useRef } from 'react';
import { useReducedMotion } from 'framer-motion';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';

interface AudioReactiveBarsProps {
  /** Number of bars to draw across the width. */
  barCount?: number;
  className?: string;
  /**
   * Optional gradient endpoints (RGB triples, 0-255). Defaults are the brand
   * SOUNDWAVE (#ff4800 orange) → NEON (#06b6d4 cyan). When both are provided
   * the bars gradient runs startColor → endColor left-to-right across the
   * spectrum. When either is missing, both defaults are used — so consumers
   * that want the artwork-sampled palette but haven't finished loading it
   * fall back cleanly to the brand pair until the sample resolves.
   */
  startColor?: [number, number, number];
  endColor?: [number, number, number];
}

const SOUNDWAVE: [number, number, number] = [255, 72, 0]; // #ff4800
const NEON: [number, number, number] = [6, 182, 212]; // #06b6d4

/**
 * Tier 2 reactive visualizer — a compact equalizer that dances to the LIVE
 * frequency spectrum of the currently-playing track (via the AnalyserNode wired
 * into AudioPlayerContext). Self-contained: fills its parent, runs an rAF loop
 * ONLY while audio is playing, and decays to flat when paused. Renders nothing
 * under reduced-motion or when the analyser isn't available — purely decorative,
 * never load-bearing.
 */
const AudioReactiveBars: React.FC<AudioReactiveBarsProps> = ({
  barCount = 40,
  className,
  startColor,
  endColor,
}) => {
  const { isPlaying, getFrequencies } = useAudioPlayer();
  const reduce = useReducedMotion();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number | null>(null);
  // Smoothed bar heights (0..1), so paused bars ease down instead of snapping.
  const levelsRef = useRef<number[]>(new Array(barCount).fill(0));
  // Gradient endpoints — sampled from artwork when provided, brand defaults
  // otherwise. All-or-nothing: if either prop is missing we use both
  // defaults so a half-supplied palette doesn't produce a jarring mix.
  const start = startColor ?? SOUNDWAVE;
  const end = endColor ?? NEON;
  // Destructured so the useEffect dep array can hold primitive numbers
  // rather than tuple-index expressions (which the exhaustive-deps rule
  // flags as complex).
  const [startR, startG, startB] = start;
  const [endR, endG, endB] = end;

  useEffect(() => {
    if (reduce) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let stopped = false;

    const draw = () => {
      if (stopped) return;
      const parent = canvas.parentElement;
      const cssW = parent?.clientWidth || canvas.clientWidth || 0;
      const cssH = parent?.clientHeight || canvas.clientHeight || 0;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      if (canvas.width !== Math.round(cssW * dpr) || canvas.height !== Math.round(cssH * dpr)) {
        canvas.width = Math.round(cssW * dpr);
        canvas.height = Math.round(cssH * dpr);
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cssW, cssH);

      const freqs = isPlaying ? getFrequencies() : null;
      const levels = levelsRef.current;
      const gap = 2;
      const barW = (cssW - gap * (barCount - 1)) / barCount;

      let anyHeight = false;
      for (let i = 0; i < barCount; i++) {
        // Sample the lower ~2/3 of the spectrum (the musically dense range).
        let target = 0;
        if (freqs && freqs.length) {
          const idx = Math.floor((i / barCount) * freqs.length * 0.66);
          target = (freqs[idx] || 0) / 255;
        }
        // Asymmetric smoothing: rise fast, fall slow — reads as a real EQ.
        const prev = levels[i];
        levels[i] = target > prev ? prev + (target - prev) * 0.6 : prev * 0.86;
        if (levels[i] > 0.01) anyHeight = true;

        const h = Math.max(2, levels[i] * cssH);
        const x = i * (barW + gap);
        const y = cssH - h;
        const t = i / (barCount - 1);
        const r = Math.round(startR + (endR - startR) * t);
        const g = Math.round(startG + (endG - startG) * t);
        const b = Math.round(startB + (endB - startB) * t);
        ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
        ctx.fillRect(x, y, barW, h);
      }

      // Keep animating while playing OR while bars are still decaying to rest.
      if (isPlaying || anyHeight) {
        rafRef.current = requestAnimationFrame(draw);
      } else {
        rafRef.current = null;
      }
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => {
      stopped = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
    // start and end are read inside the draw closure via their destructured
    // components (startR/G/B, endR/G/B) — including them in the deps ensures
    // the rAF loop restarts with fresh colors when the artwork palette
    // resolves after mount.
  }, [isPlaying, reduce, getFrequencies, barCount, startR, startG, startB, endR, endG, endB]);

  if (reduce) return null;
  return <canvas ref={canvasRef} aria-hidden className={className} />;
};

export default AudioReactiveBars;
